import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { message } from 'antd';
import PropTypes from 'prop-types';
import FeatherIcon from 'feather-icons-react';
import { getAllRelatedEntity, getQueryFieldsByType } from '../../../Api/Services/ViewApiService';
import { getAxiosInstance } from '../../../Api/ApiConfig'
import { StrikeThrougIcon } from '../../../components/Icons/Icons'

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

const ALLOWED_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'txt', 'csv', 'zip', 'rar', 'png', 'jpg', 'jpeg',
];

const validateImageFile = (file) => {
  if (!file) return false;
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    message.error('Only PNG, JPG and JPEG images are allowed');
    return false;
  }
  return true;
};

const validateAttachmentFile = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    message.error(`Only ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()} files are allowed`);
    return false;
  }
  return true;
};

// const SIGNATURE_ID = 'signature-block-unique';

// const UNSUB_ID = 'unsubscribe-block-unique';

// ── Google Fonts are loaded via a <link> injected once at module level ──────

const GFONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&family=Roboto:ital,wght@0,400;0,500;1,400&family=Open+Sans:ital,wght@0,400;0,600;1,400&family=Lato:ital,wght@0,400;0,700;1,400&family=Nunito:wght@400;600&family=Poppins:wght@400;500&family=Raleway:wght@400;500&family=Montserrat:wght@400;500&family=Source+Sans+3:wght@400;600&family=Noto+Sans:wght@400;700&family=DM+Sans:wght@400;500&family=Outfit:wght@400;500&family=Plus+Jakarta+Sans:wght@400;500&family=Figtree:wght@400;500&family=Merriweather:ital,wght@0,400;0,700;1,400&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Lora:ital,wght@0,400;0,600;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Source+Code+Pro:wght@400;500&family=Fira+Code:wght@400;500&family=JetBrains+Mono:wght@400;500&family=Ubuntu+Mono:wght@400;700&display=swap';

if (typeof document !== 'undefined' && !document.getElementById('re-gfonts')) {
  const link = document.createElement('link');
  link.id = 're-gfonts';
  link.rel = 'stylesheet';
  link.href = GFONTS_HREF;
  document.head.appendChild(link);
}

const FONT_GROUPS = [
  {
    group: 'System',
    fonts: [
      { label: 'Default', value: 'inherit' },
      { label: 'System UI', value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
      { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
      { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
      { label: 'Trebuchet MS', value: "'Trebuchet MS', sans-serif" },
      { label: 'Georgia', value: "Georgia, 'Times New Roman', serif" },
      { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
      { label: 'Courier New', value: "'Courier New', Courier, monospace" },
    ],
  },
  {
    group: 'Sans-serif',
    fonts: [
      { label: 'Inter', value: "'Inter', sans-serif" },
      { label: 'Roboto', value: "'Roboto', sans-serif" },
      { label: 'Open Sans', value: "'Open Sans', sans-serif" },
      { label: 'Lato', value: "'Lato', sans-serif" },
      { label: 'Nunito', value: "'Nunito', sans-serif" },
      { label: 'Poppins', value: "'Poppins', sans-serif" },
      { label: 'Raleway', value: "'Raleway', sans-serif" },
      { label: 'Montserrat', value: "'Montserrat', sans-serif" },
      { label: 'Source Sans 3', value: "'Source Sans 3', sans-serif" },
      { label: 'Noto Sans', value: "'Noto Sans', sans-serif" },
      { label: 'DM Sans', value: "'DM Sans', sans-serif" },
      { label: 'Outfit', value: "'Outfit', sans-serif" },
      { label: 'Plus Jakarta Sans', value: "'Plus Jakarta Sans', sans-serif" },
      { label: 'Figtree', value: "'Figtree', sans-serif" },
    ],
  },
  {
    group: 'Serif',
    fonts: [
      { label: 'Merriweather', value: "'Merriweather', serif" },
      { label: 'Playfair Display', value: "'Playfair Display', serif" },
      { label: 'Lora', value: "'Lora', serif" },
      { label: 'EB Garamond', value: "'EB Garamond', serif" },
      { label: 'Libre Baskerville', value: "'Libre Baskerville', serif" },
      { label: 'Crimson Text', value: "'Crimson Text', serif" },
    ],
  },
  {
    group: 'Monospace',
    fonts: [
      { label: 'Source Code Pro', value: "'Source Code Pro', monospace" },
      { label: 'Fira Code', value: "'Fira Code', monospace" },
      { label: 'JetBrains Mono', value: "'JetBrains Mono', monospace" },
      { label: 'Ubuntu Mono', value: "'Ubuntu Mono', monospace" },
    ],
  },
];

// Flat list kept for any code that iterates all fonts
const FONT_FAMILIES = FONT_GROUPS.flatMap(g => g.fonts);

const FONT_SIZES = [10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

const MIN_EDITOR_HEIGHT = 150;
const MAX_EDITOR_HEIGHT = 900;

const BLOCK_TYPES = [
  { label: 'Paragraph', value: 'div' },
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Blockquote', value: 'blockquote' },
  { label: 'Code', value: 'pre' },
];

const COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#ffffff',
  '#ff0000', '#ff4500', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff',
  '#9900ff', '#ff00ff', '#e6b8a2', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3',
  '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8',
  '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd', '#e06666', '#f6b26b', '#ffd966',
  '#93c47d', '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0', '#cc0000', '#e69138',
  '#f1c232', '#6aa84f', '#45818e', '#1155cc', '#2986cc', '#674ea7', '#a61c00', '#b45f06',
];

const EMOJIS = [
  '😀', '😂', '😍', '🤔', '😎', '😢', '😡', '👍', '👎', '👏', '🙏', '🎉', '🔥', '💡', '⭐',
  '✅', '❌', '📧', '📝', '📎', '🔗', '💼', '🚀', '🌟', '🎯', '📊', '💰', '🏆', '⚡', '❤️',
  '💙', '💚', '🌈', '☀️', '🎁', '🎵', '💻', '📱', '🔔', '💬', '✉️', '📌', '📋', '🗂️', '💾',
];

const S = {
  shell: (height) => ({
    display: 'flex', flexDirection: 'column',
    border: '1px solid #d9d9d9', borderRadius: 6,
    background: '#fff',
    fontSize: 13, overflow: 'hidden',
    height: height || '100%',   // falls back to 100% if no height passed
  }),
  toolbar: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center',
    padding: '4px 8px', gap: 2,
    borderBottom: '1px solid #f0f0f0', background: '#fafafa',
    flexShrink: 0,
  },
  sep: {
    width: 1, height: 20, background: '#e0e0e0', margin: '0 3px', flexShrink: 0,
  },
  tb: (active) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, border: active ? '1px solid #1677ff' : '1px solid transparent',
    borderRadius: 4, background: active ? '#e6f4ff' : 'transparent',
    color: active ? '#1677ff' : '#595959',
    cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0,
    transition: 'all 0.1s',
  }),
  tbSelect: {
    height: 28, padding: '0 6px', border: '1px solid #d9d9d9',
    borderRadius: 4, background: '#fff', fontSize: 12,
    color: '#1f1f1f', cursor: 'pointer',
  },
  // ─── KEY FIX: editorWrap is position:relative, overflow:hidden ───
  editorWrap: {
    flex: 1,           // grows to fill whatever shell has left after toolbar+statusbar
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
  },
  editor: (disabled) => ({   // ← remove height param
    position: 'absolute',
    inset: 0,
    outline: 'none',
    padding: '14px 16px',
    fontSize: 14, lineHeight: 1.4, color: '#1f1f1f',
    overflowY: 'auto',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.55 : 1,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  }),
  resizeHandle: {
    height: 10, flexShrink: 0, cursor: 'ns-resize', touchAction: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#fafafa', borderTop: '1px solid #f0f0f0',
  },
  resizeGrip: {
    width: 32, height: 3, borderRadius: 2, background: '#c8c8c8',
  },
  statusbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    padding: '3px 12px', borderTop: '1px solid #f0f0f0',
    background: '#fafafa', fontSize: 11, color: '#8c8c8c', flexShrink: 0,
  },
  overlay: (open) => ({
    display: open ? 'flex' : 'none',
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.35)',
    alignItems: 'center', justifyContent: 'center',
  }),
  modal: {
    background: '#fff', borderRadius: 8,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    padding: 24, minWidth: 400, maxWidth: 520, width: '90%',
  },
  modalTitle: { fontSize: 15, fontWeight: 600, marginBottom: 16 },
  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, color: '#595959', fontWeight: 500, marginBottom: 4 },
  input: {
    width: '100%', height: 34, padding: '0 10px',
    border: '1px solid #d9d9d9', borderRadius: 4,
    fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
  },
  row: { display: 'flex', gap: 10 },
  btnRow: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 },
  btnCancel: {
    padding: '5px 14px', border: '1px solid #d9d9d9', borderRadius: 4,
    background: '#fff', cursor: 'pointer', fontSize: 13,
  },
  btnPrimary: {
    padding: '5px 16px', border: 'none', borderRadius: 4,
    background: '#1677ff', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500,
  },
  varDropdown: (pos) => ({
    position: 'fixed', zIndex: 9000,
    top: pos.top, left: pos.left,
    background: '#fff', border: '1px solid #d9d9d9',
    borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    maxHeight: 240, overflowY: 'auto', minWidth: 280,
  }),
  varItem: (highlighted) => ({
    padding: '7px 12px', cursor: 'pointer', fontSize: 12,
    background: highlighted ? '#e6f4ff' : '#fff',
    color: highlighted ? '#1677ff' : '#1f1f1f',
    borderBottom: '1px solid #f5f5f5',
  }),
  varHeader: {
    padding: '6px 12px', fontSize: 11, color: '#8c8c8c',
    fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
    borderBottom: '1px solid #f0f0f0', background: '#fafafa',
  },
  colorGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(8, 24px)',
    gap: 3, marginBottom: 12,
  },
  swatch: (color) => ({
    width: 24, height: 24, borderRadius: 3, background: color, cursor: 'pointer',
    border: '1px solid rgba(0,0,0,0.12)',
  }),
  emojiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)',
    gap: 2, maxHeight: 200, overflowY: 'auto',
  },
  emojiBtn: {
    fontSize: 20, background: 'none', border: 'none',
    cursor: 'pointer', padding: 5, borderRadius: 4, textAlign: 'center',
  },
  tableGrid: { display: 'grid', gridTemplateColumns: 'repeat(8, 22px)', gap: 2, marginBottom: 12 },
  tableCell: (sel) => ({
    width: 22, height: 22, border: '1px solid #d9d9d9', borderRadius: 2,
    cursor: 'pointer', background: sel ? '#e6f4ff' : '#fff',
    borderColor: sel ? '#1677ff' : '#d9d9d9',
  }),
  attachChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 8px', background: '#f0f0f0',
    borderRadius: 20, fontSize: 11, color: '#595959', margin: '2px 4px 2px 0',
  },
  attachChipX: {
    border: 'none', background: 'none', cursor: 'pointer',
    color: '#8c8c8c', fontSize: 12, padding: 0, lineHeight: 1,
  },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

// function parseHtml(html) {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(`<div id="__r__">${html}</div>`, 'text/html');
//   return doc.getElementById('__r__');
// }

function normalizeUrl(raw) {
  let url = raw.trim();
  if (url.startsWith('www.')) url = `https://${url}`;
  if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(url) && !url.startsWith('http')) url = `https://${url}`;
  return url;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

function useAttributes(selectedEntity, page) {
  const [attributesList, setAttributesList] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const attributesRef = useRef([]);
  const getQueryFields = useCallback(async (entity) => {
    try {
      const response = await getQueryFieldsByType(entity, 'Template');
      return response.data;
    } catch (err) {
      message.error(String(err));
      return [];
    }
  }, []);
  function buildAttributes(entity, mainAttrs, relatedEntities) {
    const result = [];
    mainAttrs.forEach(attr => {
      result.push({ label: `${entity}.${attr.fieldLable}`, value: `${entity}.${attr.filedValue}` });
    });
    relatedEntities.forEach(rel => {
      rel.attributes.forEach(attr => {
        result.push({ label: `${entity}.${rel.entity}.${attr.fieldLable}`, value: `${entity}.${rel.entity}.${attr.filedValue}` });
      });
    });
    return result;
  }
  useEffect(() => {
    if (!selectedEntity || page !== 'Template') return;
    let cancelled = false;
    async function load() {
      setLoadingAttributes(true);
      try {
        const mainAttributes = await getQueryFields(selectedEntity);
        const relatedResponse = await getAllRelatedEntity(selectedEntity, 'Template');
        const relatedEntities = (relatedResponse || []).filter(rel => rel?.entity && rel.entity.trim() !== '');
        const relatedAttributesArr = await Promise.all(
          relatedEntities.map(async rel => ({ entity: rel.entity, attributes: (await getQueryFields(rel.entity)) || [] }))
        );
        if (!cancelled) {
          const list = buildAttributes(selectedEntity, mainAttributes || [], relatedAttributesArr);
          setAttributesList(list);
          attributesRef.current = list;
        }
      } catch (err) {
        message.error(String(err));
        if (!cancelled) setAttributesList([]);
      } finally {
        if (!cancelled) setLoadingAttributes(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedEntity, page, getQueryFields]);
  useEffect(() => { attributesRef.current = attributesList; }, [attributesList]);
  return { attributesList, attributesRef, loadingAttributes };
}

function useImageUpload() {
  const uploadImage = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await getAxiosInstance().post(
      `/usermgmt/uploadFile`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return { url: response.data.mediaUrl, name: response.data.mediaName };
  }, []);
  const insertImageIntoEditor = useCallback((editorEl, url, name) => {
    editorEl.focus();
    document.execCommand('insertHTML', false,
      `<img src="${url}" alt="${name || ''}" style="max-width:100%;height:auto;display:inline-block;" />`
    );
  }, []);
  const replacePlaceholder = useCallback(async (file, imgEl) => {
    try {
      const { url } = await uploadImage(file);
      imgEl.src = url;
      imgEl.removeAttribute('data-uploading');
    } catch (err) {
      message.error(err?.response?.data?.message?.message || 'Image upload failed');
      imgEl.remove();
    }
  }, [uploadImage]);
  return { uploadImage, insertImageIntoEditor, replacePlaceholder };
}

// ─── Modals ─────────────────────────────────────────────────────────────────

function Modal({ open, onClose, title, children, width }) {
  if (!open) return null;
  return (
    // <div style={S.overlay(open)} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div
      style={S.overlay(open)}
      role="button"
      tabIndex={0}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.target === e.currentTarget) onClose();
        }
        if (e.key === 'Escape') {
          onClose();
        }
      }}
    >
      <div style={{ ...S.modal, minWidth: width || 400 }}>
        <div style={S.modalTitle}>{title}</div>
        {children}
      </div>
    </div>
  );
}

Modal.propTypes = {
  open: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
  width: PropTypes.number.isRequired,
};

function LinkModal({ open, onClose, onInsert, clickTrackUrl, initialData, IsTrackUrl }) {
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [track, setTrack] = useState(false);
  useEffect(() => {
    if (open && initialData) {
      let rawUrl = initialData.url || '';
      let isTracked = false;
      if (clickTrackUrl && rawUrl.startsWith(clickTrackUrl)) { rawUrl = rawUrl.slice(clickTrackUrl.length); isTracked = true; }
      setUrl(rawUrl); setText(initialData.text || ''); setTitle(initialData.title || ''); setTrack(isTracked);
    }
  }, [open, initialData, clickTrackUrl]);
  const handleSubmit = () => {
    if (!url.trim()) { message.warning('Please enter a URL'); return; }
    let finalUrl = normalizeUrl(url);
    if (track && clickTrackUrl) finalUrl = clickTrackUrl + finalUrl;
    onInsert({ url: finalUrl, text: text.trim(), title: title.trim(), tracked: track });
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title={initialData?.isEdit ? 'Edit link' : 'Insert link'}>
      <div style={S.field}><label style={S.label}>URL *</label>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <input style={S.input} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com" autoFocus /></div>
      <div style={S.field}><label style={S.label}>Display text</label><input style={S.input} value={text} onChange={e => setText(e.target.value)} placeholder="Leave empty to use selected text" /></div>
      <div style={S.field}><label style={S.label}>Title (tooltip)</label><input style={S.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Optional" /></div>
      {IsTrackUrl !== "No" ? <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <input type="checkbox" id="track-cb" checked={track} onChange={e => setTrack(e.target.checked)} />
        <label htmlFor="track-cb" style={{ fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>Add click tracking URL</label>
      </div> : ""}
      {/* {track && <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, wordBreak: 'break-all' }}>Final URL: {clickTrackUrl}{normalizeUrl(url)}</div>} */}
      <div style={S.btnRow}>
        <button type="button" style={S.btnCancel} onClick={onClose}>Cancel</button>
        <button type="button" style={S.btnPrimary} onClick={handleSubmit}>{initialData?.isEdit ? 'Update' : 'Insert'}</button>
      </div>
    </Modal>
  );
}

LinkModal.propTypes = {
  open: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
  clickTrackUrl: PropTypes.string.isRequired,
  initialData: PropTypes.object.isRequired,
  IsTrackUrl: PropTypes.string.isRequired,
};

function ImageUrlModal({ open, onClose, onInsert }) {
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [width, setWidth] = useState('');
  useEffect(() => { if (!open) { setUrl(''); setAlt(''); setWidth(''); } }, [open]);
  const handleInsert = () => {
    if (!url.trim()) { message.warning('Please enter an image URL'); return; }
    onInsert({ url: url.trim(), alt: alt.trim(), width: width.trim() });
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title="Insert image by URL">
      <div style={S.field}><label style={S.label}>Image URL *</label>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <input style={S.input} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/image.png" autoFocus /></div>
      <div style={S.field}><label style={S.label}>Alt text</label><input style={S.input} value={alt} onChange={e => setAlt(e.target.value)} placeholder="Describe the image" /></div>
      <div style={S.field}><label style={S.label}>Width (optional)</label><input style={S.input} value={width} onChange={e => setWidth(e.target.value)} placeholder="e.g. 100% or 400px" /></div>
      <div style={S.btnRow}>
        <button type="button" style={S.btnCancel} onClick={onClose}>Cancel</button>
        <button type="button" style={S.btnPrimary} onClick={handleInsert}>Insert</button>
      </div>
    </Modal>
  );
}

ImageUrlModal.propTypes = {
  open: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
};

function TableModal({ open, onClose, onInsert }) {
  const [hoverR, setHoverR] = useState(0);
  const [hoverC, setHoverC] = useState(0);
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [header, setHeader] = useState(true);
  const [stripe, setStripe] = useState(true);
  const handleInsert = () => { onInsert({ rows, cols, header, stripe }); onClose(); };
  return (
    <Modal open={open} onClose={onClose} title="Insert table" width={440}>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>{hoverR && hoverC ? `${hoverR} × ${hoverC}` : 'Hover to select size'}</div>
      <div style={S.tableGrid}>
        {Array.from({ length: 48 }, (_, i) => {
          const r = Math.floor(i / 8) + 1; const c = (i % 8) + 1; const sel = r <= hoverR && c <= hoverC;
          return <div key={i} style={S.tableCell(sel)} aria-label={c}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleInsert()
              }
            }}
            onMouseEnter={() => { setHoverR(r); setHoverC(c); setRows(r); setCols(c); }}
            onClick={handleInsert} />;
        })}
      </div>
      <div style={S.row}>
        <div style={{ ...S.field, flex: 1 }}><label style={S.label}>Rows</label><input style={S.input} type="number" min={1} max={20} value={rows} onChange={e => setRows(+e.target.value)} /></div>
        <div style={{ ...S.field, flex: 1 }}><label style={S.label}>Columns</label><input style={S.input} type="number" min={1} max={10} value={cols} onChange={e => setCols(+e.target.value)} /></div>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
        <label style={{ fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}><input type="checkbox" checked={header} onChange={e => setHeader(e.target.checked)} />Header row</label>
        <label style={{ fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}><input type="checkbox" checked={stripe} onChange={e => setStripe(e.target.checked)} />Striped rows</label>
      </div>
      <div style={S.btnRow}>
        <button type="button" style={S.btnCancel} onClick={onClose}>Cancel</button>
        <button type="button" style={S.btnPrimary} onClick={handleInsert}>Insert table</button>
      </div>
    </Modal>
  );
}

TableModal.propTypes = {
  open: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
};

function ColorPickerModal({ open, onClose, onSelect, title }) {
  const [custom, setCustom] = useState('#000000');
  return (
    <Modal open={open} onClose={onClose} title={title || 'Pick colour'} width={260}>
      <div style={S.colorGrid}>{COLORS.map(c => <div key={c} style={S.swatch(c)} title={c} aria-label={`Select ${c} color`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onSelect(c);
            onClose();
          }
        }}
        onClick={() => { onSelect(c); onClose(); }} />)}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={S.label}>Custom</label>
        <input type="color" value={custom} onChange={e => setCustom(e.target.value)} style={{ width: 36, height: 28, border: 'none', cursor: 'pointer', padding: 0 }} />
        <button type="button" style={S.btnPrimary} onClick={() => { onSelect(custom); onClose(); }}>Apply</button>
      </div>
    </Modal>
  );
}

ColorPickerModal.propTypes = {
  open: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

function EmojiModal({ open, onClose, onSelect }) {
  return (
    <Modal open={open} onClose={onClose} title="Insert emoji" width={320}>
      <div style={S.emojiGrid}>{EMOJIS.map(e => <button type="button" key={e} style={S.emojiBtn} onClick={() => { onSelect(e); onClose(); }}>{e}</button>)}</div>
    </Modal>
  );
}

EmojiModal.propTypes = {
  open: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function VariableAutocomplete({ editorRef, attributesRef, loadingAttributes, enabled }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [highlighted, setHighlighted] = useState(0);
  const triggerRangeRef = useRef(null);

  // ─── Variable Autocomplete ───────────────────────────────────────────────────
  const selectItem = useCallback((item) => {
    const ref = triggerRangeRef.current;
    if (!ref) return;
    const { container, atIdx, query } = ref;
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.setStart(container, atIdx);
    range.setEnd(container, atIdx + 1 + query.length);
    sel.removeAllRanges();
    sel.addRange(range);
    document.execCommand('insertHTML', false, `{{${item.value}}}`);
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const el = editorRef.current;
    if (!el) return;
    const onKeyDown = (e) => {
      if (!open) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, items.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
      else if (e.key === 'Enter' && items.length) { e.preventDefault(); selectItem(items[highlighted]); }
      else if (e.key === 'Escape') { setOpen(false); }
    };
    const onKeyUp = (e) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) return;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) { setOpen(false); return; }
      const range = sel.getRangeAt(0);
      const text = range.startContainer.textContent || '';
      const offset = range.startOffset;
      const before = text.slice(0, offset);
      const atIdx = before.lastIndexOf('@');
      if (atIdx === -1) { setOpen(false); return; }
      const query = before.slice(atIdx + 1);
      if (query.includes(' ')) { setOpen(false); return; }
      const caretRange = range.cloneRange();
      caretRange.collapse(true);
      const rect = caretRange.getBoundingClientRect();
      triggerRangeRef.current = { container: range.startContainer, atIdx, query };
      const list = attributesRef.current || [];
      const filtered = query ? list.filter(a => a.label.toLowerCase().includes(query.toLowerCase())) : list;
      setItems(filtered.slice(0, 50));
      setHighlighted(0);
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
      setOpen(filtered.length > 0 || loadingAttributes);
    };
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('keyup', onKeyUp);
    return () => { el.removeEventListener('keydown', onKeyDown); el.removeEventListener('keyup', onKeyUp); };
  }, [enabled, open, items, highlighted, attributesRef, loadingAttributes, editorRef]);

  if (!open) return null;
  return (
    <div style={S.varDropdown(pos)} role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
        }
      }}
      onMouseDown={e => e.preventDefault()}>
      {loadingAttributes
        ? <div style={S.varHeader}>Loading attributes…</div>
        : items.length === 0
          ? <div style={{ ...S.varItem(false), color: '#8c8c8c' }}>No attributes match</div>
          : <>
            <div style={S.varHeader}>Variables - type to filter</div>
            {items.map((item, i) => (
              <div key={item.value} style={S.varItem(i === highlighted)}
                onMouseEnter={() => setHighlighted(i)} onMouseDown={() => selectItem(item)}
                role="button"
                tabIndex={0}>
                {item.label}
              </div>
            ))}
          </>
      }
    </div>
  );
}

VariableAutocomplete.propTypes = {
  editorRef: PropTypes.object.isRequired,
  attributesRef: PropTypes.object.isRequired,
  loadingAttributes: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
};
// ─── Font Dropdown ───────────────────────────────────────────────────────────

const fontDropStyles = {
  wrap: { position: 'relative', flexShrink: 0 },
  btn: (disabled) => ({
    display: 'flex', alignItems: 'center', gap: 4,
    height: 28, padding: '0 8px', minWidth: 140, maxWidth: 160,
    border: '1px solid #d9d9d9', borderRadius: 4,
    background: '#fff', color: disabled ? '#bfbfbf' : '#1f1f1f',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12,
    whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left',
    opacity: disabled ? 0.5 : 1,
  }),
  // panel is rendered fixed - position computed from trigger rect in component
  panel: (rect) => {
    const panelHeight = 320; // approx max height (search + list)
    const spaceBelow = window.innerHeight - rect.bottom - 6;
    const openUpward = spaceBelow < panelHeight && rect.top > panelHeight;
    const top = openUpward ? rect.top - panelHeight - 3 : rect.bottom + 3;
    // clamp left so panel never overflows the right edge
    const left = Math.min(rect.left, window.innerWidth - 244);
    return {
      position: 'fixed', top, left, zIndex: 9500,
      width: 240, background: '#fff',
      border: '1px solid #d9d9d9', borderRadius: 6,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      maxHeight: Math.min(panelHeight, openUpward ? rect.top - 6 : spaceBelow),
    };
  },
  searchWrap: { padding: '7px 8px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  searchInp: {
    width: '100%', height: 26, padding: '0 8px',
    border: '1px solid #d9d9d9', borderRadius: 4,
    fontSize: 12, outline: 'none', boxSizing: 'border-box',
  },
  list: { overflowY: 'auto', maxHeight: 260 },
  groupHdr: {
    padding: '4px 10px 2px', fontSize: 10, fontWeight: 600,
    letterSpacing: '0.07em', textTransform: 'uppercase',
    color: '#8c8c8c', background: '#fafafa',
    borderBottom: '1px solid #f0f0f0',
    position: 'sticky', top: 0, zIndex: 1,
  },
  item: (active) => ({
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    padding: '6px 12px', cursor: 'pointer',
    borderBottom: '1px solid #f5f5f5',
    background: active ? '#e6f4ff' : '#fff',
  }),
  itemName: (font) => ({ fontSize: 13, fontWeight: 500, color: '#1f1f1f', fontFamily: font }),
  itemPrev: (font) => ({ fontSize: 13, color: '#8c8c8c', fontFamily: font }),
};

function FontDropdown({ currentFont, onSelect, onOpen, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const currentLabel = useMemo(
    () => FONT_FAMILIES.find(f => f.value === currentFont)?.label || 'Default',
    [currentFont],
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return FONT_GROUPS
      .map(g => ({ ...g, fonts: g.fonts.filter(f => !q || f.label.toLowerCase().includes(q)) }))
      .filter(g => g.fonts.length > 0);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll active item into view when opening
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [open]);

  const handleSelect = (fontValue) => {
    onSelect(fontValue);
    setOpen(false);
    setQuery('');
  };

  const handleToggle = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      setRect(btnRef.current.getBoundingClientRect());
    }
    setOpen(o => !o);
  };

  // ── FIX: save the selection on mousedown, BEFORE the browser clears it.
  //    onClick fires after mousedown has already moved focus away from the
  //    editor and cleared window.getSelection(), so saving there is too late.
  const handleMouseDown = () => {
    if (disabled) return;
    if (!open) {
      onOpen?.();   // save selection while it still exists
    }
    // Do NOT preventDefault here - we still want the button to receive focus
    // and the click event to fire normally.
  };

  return (
    <div ref={wrapRef} style={fontDropStyles.wrap}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        style={fontDropStyles.btn(disabled)}
        onMouseDown={handleMouseDown}
        onClick={handleToggle}
        title="Font family"
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: currentFont }}>
          {currentLabel}
        </span>
        {open
          ? <FeatherIcon icon="chevron-up" size={11} color="#8c8c8c" style={{ flexShrink: 0 }} />
          : <FeatherIcon icon="chevron-down" size={11} color="#8c8c8c" style={{ flexShrink: 0 }} />
        }
      </button>

      {open && rect && (
        <div role="button"
          tabIndex={0}
          style={fontDropStyles.panel(rect)}
          onMouseDown={e => e.preventDefault()}
        >
          <div style={fontDropStyles.searchWrap}>
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              style={fontDropStyles.searchInp}
              placeholder="Search fonts…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } e.stopPropagation(); }}
            />
          </div>
          <div ref={listRef} style={fontDropStyles.list}>
            {filtered.map(g => (
              <React.Fragment key={g.group}>
                <div style={fontDropStyles.groupHdr}>{g.group}</div>
                {g.fonts.map(f => (
                  <div
                    key={f.value}
                    data-active={f.value === currentFont ? 'true' : undefined}
                    style={fontDropStyles.item(f.value === currentFont)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSelect(f.value)
                      }
                    }}
                    onClick={() => handleSelect(f.value)}
                  >
                    <span style={fontDropStyles.itemName(f.value)}>{f.label}</span>
                    <span style={fontDropStyles.itemPrev(f.value)}>Aa</span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

FontDropdown.propTypes = {
  currentFont: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};
// ─── ToolbarDropdown - groups multiple buttons behind one trigger ─────────────

const tbDropStyles = {
  wrap: { position: 'relative', flexShrink: 0 },
  trigger: (active, disabled) => ({
    display: 'flex', alignItems: 'center', gap: 3,
    height: 28, padding: '0 7px',
    border: active ? '1px solid #1677ff' : '1px solid #d9d9d9',
    borderRadius: 4,
    background: active ? '#e6f4ff' : '#fff',
    color: active ? '#1677ff' : '#595959',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, flexShrink: 0,
    opacity: disabled ? 0.35 : 1,
    whiteSpace: 'nowrap',
  }),
  // panel rendered fixed - position computed from trigger rect in component
  panel: (rect) => {
    const panelHeight = 200; // approx max for list/align/insert menus
    const spaceBelow = window.innerHeight - rect.bottom - 6;
    const openUpward = spaceBelow < panelHeight && rect.top > panelHeight;
    const top = openUpward ? rect.top - panelHeight - 3 : rect.bottom + 3;
    // clamp left so panel never overflows the right edge
    const left = Math.min(rect.left, window.innerWidth - 168);
    return {
      position: 'fixed', top, left, zIndex: 9500,
      background: '#fff', border: '1px solid #d9d9d9',
      borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      padding: 6, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160,
      maxHeight: Math.min(panelHeight, openUpward ? rect.top - 6 : spaceBelow),
      overflowY: 'auto',
    };
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '5px 10px', borderRadius: 4, cursor: 'pointer',
    fontSize: 13, color: '#1f1f1f',
  },
};

function ToolbarDropdown({ label, icon, items, disabled }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const anyActive = items.some(it => it.active);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    setOpen(o => !o);
  };

  return (
    <div ref={wrapRef} style={tbDropStyles.wrap}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        style={tbDropStyles.trigger(anyActive, disabled)}
        onMouseDown={e => { e.preventDefault(); handleToggle(); }}
        title={label}
      >
        {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
        <span style={{ fontSize: 11, color: 'inherit' }}>{label}</span>
        <FeatherIcon icon="chevron-down" size={11} color={anyActive ? '#1677ff' : '#8c8c8c'} style={{ marginLeft: 1, flexShrink: 0 }} />
      </button>

      {open && rect && (
        <div style={tbDropStyles.panel(rect)} onMouseDown={e => e.preventDefault()} role="button"
          tabIndex={0}>
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                ...tbDropStyles.row,
                background: item.active ? '#e6f4ff' : 'transparent',
                color: item.active ? '#1677ff' : '#1f1f1f',
              }}
              role="button"
              tabIndex={0}
              onMouseDown={e => { e.preventDefault(); item.onClick(); setOpen(false); }}
              onMouseEnter={e => { if (!item.active) e.currentTarget.style.background = '#f5f5f5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = item.active ? '#e6f4ff' : 'transparent'; }}
            >
              {item.icon && <span style={{ display: 'flex', alignItems: 'center', width: 18, flexShrink: 0 }}>{item.icon}</span>}
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

ToolbarDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.any.isRequired,
  items: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
};
// ─── Toolbar helpers ─────────────────────────────────────────────────────────

function TbBtn({ title, icon, active, onClick, disabled }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" title={title}
      // onMouseDown={e => { e.preventDefault(); }}  // keep editor selection alive
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ ...S.tb(active), background: active ? '#e6f4ff' : hover ? '#f5f5f5' : 'transparent', opacity: disabled ? 0.35 : 1, pointerEvents: disabled ? 'none' : undefined }}>
      {icon}
    </button>
  );
}

TbBtn.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.any.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

function Sep() { return <div style={S.sep} />; }

function buildTableHtml({ rows, cols, header, stripe }) {
  const borderStyle = 'border:1px solid #d9d9d9;padding:8px 12px;';
  let html = `<table style="width:100%;border-collapse:collapse;font-size:13px;margin:12px 0;">`;
  if (header) {
    html += '<thead><tr>';
    for (let c = 0; c < cols; c += 1) html += `<th style="${borderStyle}background:#fafafa;font-weight:600;text-align:left;">Header ${c + 1}</th>`;
    html += '</tr></thead>';
  }
  html += '<tbody>';
  for (let r = 0; r < rows; r += 1) {
    const bg = stripe && r % 2 === 1 ? 'background:#fafafa;' : '';
    html += '<tr>';
    for (let c = 0; c < cols; c += 1) html += `<td style="${borderStyle}${bg}">Cell</td>`;
    html += '</tr>';
  }
  html += '</tbody></table><div><br></div>';
  return html;
}

buildTableHtml.propTypes = {
  rows: PropTypes.number.isRequired,
  cols: PropTypes.number.isRequired,
  header: PropTypes.bool.isRequired,
  stripe: PropTypes.any.isRequired,
};

// ─── Main component ──────────────────────────────────────────────────────────

function RichEmailEditor({ IsTrackUrl,
  placeholder, setContent, content, edit = false,
  page, setFiles, height, selectedEntity,
}) {
  const editorRef = useRef(null);
  const wrapRef = useRef(null);           // ref on the position:relative wrapper
  const contentRef = useRef(content);
  const fileInputRef = useRef(null);
  const attachInputRef = useRef(null);
  const clickTrackUrl = process.env.REACT_APP_CLICK_URL || '';
  const [activeFormats, setActiveFormats] = useState({});
  // const [wordCount, setWordCount] = useState(0);
  const [modals, setModals] = useState({ link: false, imageUrl: false, table: false, textColor: false, highlightColor: false, emoji: false });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBox, setImageBox] = useState(null);   // coords relative to wrapRef
  const resizeStateRef = useRef(null);
  const openModal = (name) => setModals(m => ({ ...m, [name]: true }));
  const closeModal = (name) => setModals(m => ({ ...m, [name]: false }));
  const [linkInitial, setLinkInitial] = useState(null);
  const savedRangeRef = useRef(null);
  const { attributesRef, loadingAttributes } = useAttributes(selectedEntity, page);
  const { uploadImage, insertImageIntoEditor, replacePlaceholder } = useImageUpload();

  // ── Editor box resize (drag handle at the bottom, like a textarea) ────────
  const shellRef = useRef(null);
  const boxResizeRef = useRef(null);
  const [customHeight, setCustomHeight] = useState(null);

  const startBoxResize = useCallback((e) => {
    e.preventDefault();
    boxResizeRef.current = {
      startY: e.clientY,
      startH: shellRef.current?.getBoundingClientRect().height || 0,
    };
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!boxResizeRef.current) return;
      const { startY, startH } = boxResizeRef.current;
      setCustomHeight(
        Math.min(MAX_EDITOR_HEIGHT, Math.max(MIN_EDITOR_HEIGHT, startH + (e.clientY - startY)))
      );
    };
    const onUp = () => {
      if (!boxResizeRef.current) return;
      boxResizeRef.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = '';
    };
  }, []);

  useEffect(() => { contentRef.current = content; }, [content]);
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== content) el.innerHTML = content || '';
  }, [content]);


  // ── FIX: getAlignFromDOM - queryCommandState('justifyLeft') always returns
  //    false on default-aligned content, so we read text-align from the DOM instead.
  const getAlignFromDOM = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return 'left';
    let node = sel.getRangeAt(0).commonAncestorContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    const el = editorRef.current;
    const block = node.closest?.('p,div,h1,h2,h3,h4,h5,h6,li,blockquote,pre') || node;
    if (!el?.contains(block) || block === el) return 'left';
    return (block instanceof HTMLElement && block.style?.textAlign) || 'left';
  }, []);

  // ── FIX: getListStateFromDOM - queryCommandState for lists is unreliable in
  //    some browsers; traverse the DOM instead for accurate active state.
  const getListStateFromDOM = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return { ul: false, ol: false };
    let node = sel.getRangeAt(0).commonAncestorContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    return {
      ul: !!node.closest?.('ul'),
      ol: !!node.closest?.('ol'),
    };
  }, []);

  const syncFormats = useCallback(() => {
    const align = getAlignFromDOM();
    const { ul, ol } = getListStateFromDOM();
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikethrough: document.queryCommandState('strikethrough'),
      // ── FIX: derive alignment from DOM style, not queryCommandState ──
      justifyLeft: align === 'left' || align === '',
      justifyCenter: align === 'center',
      justifyRight: align === 'right',
      justifyFull: align === 'justify',
      // ── FIX: derive list state from DOM traversal ──
      insertUnorderedList: ul,
      insertOrderedList: ol,
    });
  }, [getAlignFromDOM, getListStateFromDOM]);

  // ── Core helpers ──────────────────────────────────────────────────────────

  const handleInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    setContent(html);
    contentRef.current = html;
    // const text = el.innerText || '';
    // setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);
    syncFormats();
  }, [setContent]);

  const exec = useCallback((cmd, val = null) => {
    if (!edit) return;
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  }, [edit, handleInput]);

  // ── FIX 1: applyAlign - apply text-align to ALL block elements touched by
  //    the current selection (not just the single block at the caret). This
  //    makes alignment work correctly for both collapsed cursors and multi-line
  //    selections. execCommand('justifyX') is unreliable for "left" (no-op on
  //    default-left content), so we write style.textAlign directly.
  // const applyAlign = useCallback((align) => {
  //   if (!edit) return;
  //   const el = editorRef.current;
  //   if (!el) return;
  //   el.focus();
  //   const sel = window.getSelection();
  //   if (!sel || !sel.rangeCount) return;
  //   const range = sel.getRangeAt(0);

  //   // Collect all block-level elements inside (or touching) the selection.
  //   const BLOCK_SELECTOR = 'p,div,h1,h2,h3,h4,h5,h6,li,blockquote,pre';
  //   const blocksToAlign = new Set();

  //   // Helper: walk up to find the nearest block ancestor within the editor.
  //   const nearestBlock = (node) => {
  //     let n = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  //     while (n && n !== el) {
  //       if (n.matches?.(BLOCK_SELECTOR)) return n;
  //       n = n.parentElement;
  //     }
  //     return null;
  //   };

  //   if (range.collapsed) {
  //     // Collapsed cursor - just align the single block the caret sits in.
  //     const b = nearestBlock(range.startContainer);
  //     if (b) blocksToAlign.add(b);
  //   } else {
  //     // Multi-block selection: walk every node in the range and collect blocks.
  //     const walker = document.createTreeWalker(
  //       range.commonAncestorContainer.nodeType === Node.TEXT_NODE
  //         ? range.commonAncestorContainer.parentElement
  //         : range.commonAncestorContainer,
  //       NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
  //       null
  //     );
  //     let node = walker.nextNode();
  //     while (node) {
  //       if (range.intersectsNode(node)) {
  //         const b = nearestBlock(node);
  //         if (b) blocksToAlign.add(b);
  //       }
  //       node = walker.nextNode();
  //     }
  //     // Always include the start/end containers' blocks too.
  //     [range.startContainer, range.endContainer].forEach(c => {
  //       const b = nearestBlock(c);
  //       if (b) blocksToAlign.add(b);
  //     });
  //   }

  //   blocksToAlign.forEach(b => { b.style.textAlign = align; });
  //   handleInput();
  //   syncFormats();
  // }, [edit, handleInput, syncFormats]);
  const applyAlign = useCallback((align) => {
    if (!edit) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);

    const BLOCK_SELECTOR = 'p,div,h1,h2,h3,h4,h5,h6,li,blockquote,pre';

    // Walk up to find the nearest block ancestor within the editor (not the editor itself).
    const nearestBlock = (node) => {
      let n = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      while (n && n !== el) {
        if (n.matches?.(BLOCK_SELECTOR)) return n;
        n = n.parentElement;
      }
      return null;
    };

    // Helper: collect all direct-child nodes of the editor that are inline
    // (text nodes or non-block elements). These are nodes that have no block
    // wrapper and need to be grouped into a <div> for alignment to work.
    const wrapInlineChildrenAroundNode = (anchorNode) => {
      // Gather all consecutive inline siblings that form one visual paragraph.
      // We collect all direct children of el that are NOT block elements.
      const inlineChildren = Array.from(el.childNodes).filter(n => {
        if (n.nodeType === Node.TEXT_NODE) return true;
        if (n.nodeType === Node.ELEMENT_NODE) {
          const tag = n.tagName.toLowerCase();
          const blockTags = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'table', 'hr'];
          return !blockTags.includes(tag);
        }
        return false;
      });
      if (inlineChildren.length === 0) return null;

      // Save caret position as offset within the anchor text node
      const anchorOffset = range.startOffset;

      // Wrap all inline children into a single div
      const div = document.createElement('div');
      div.style.textAlign = align;
      el.insertBefore(div, inlineChildren[0]);
      inlineChildren.forEach(n => div.appendChild(n));

      // Restore caret: find anchorNode inside the new div and set offset
      const restoreRange = document.createRange();
      try {
        restoreRange.setStart(anchorNode, anchorOffset);
        restoreRange.collapse(true);
      } catch (_) {
        restoreRange.selectNodeContents(div);
        restoreRange.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(restoreRange);
      return div;
    };

    const blocksToAlign = new Set();

    if (range.collapsed) {
      const b = nearestBlock(range.startContainer);
      if (b) {
        blocksToAlign.add(b);
      } else {
        // Bare text/inline directly in editor - wrap into a div scoped to this line
        wrapInlineChildrenAroundNode(range.startContainer);
        handleInput();
        syncFormats();
        return;
      }
    } else {
      const allBlocks = Array.from(el.querySelectorAll(BLOCK_SELECTOR));
      allBlocks.forEach(block => {
        const blockRange = document.createRange();
        blockRange.selectNodeContents(block);
        const startToBlockEnd = range.compareBoundaryPoints(Range.END_TO_START, blockRange);
        const endToBlockStart = range.compareBoundaryPoints(Range.START_TO_END, blockRange);
        if (startToBlockEnd < 0 && endToBlockStart > 0) {
          const isAncestorOfAlreadyAdded = Array.from(blocksToAlign).some(b => block.contains(b));
          if (!isAncestorOfAlreadyAdded) {
            blocksToAlign.forEach(b => { if (b.contains(block)) blocksToAlign.delete(b); });
            blocksToAlign.add(block);
          }
        }
      });
      if (blocksToAlign.size === 0) {
        [range.startContainer, range.endContainer].forEach(c => {
          const b = nearestBlock(c);
          if (b) blocksToAlign.add(b);
        });
      }
      if (blocksToAlign.size === 0) {
        // Bare text selection - wrap and align
        wrapInlineChildrenAroundNode(range.startContainer);
        handleInput();
        syncFormats();
        return;
      }
    }

    blocksToAlign.forEach(b => { b.style.textAlign = align; });
    handleInput();
    syncFormats();
  }, [edit, handleInput, syncFormats]);
  // ── FIX 3: toggleList - correctly handles:
  //    a) Toggling off: unwrap only the list the cursor is in (not all lists).
  //    b) Switching ul ↔ ol: change only the specific list at the cursor.
  //    c) Creating: insertHTML fallback when execCommand is a no-op.
  const toggleList = useCallback((type) => {
    if (!edit) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    let node = range.commonAncestorContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    // Only look at the closest list ancestor of the CURSOR position,
    // not any arbitrary list in the document.
    const existingList = node.closest?.('ul,ol');

    if (existingList && el.contains(existingList)) {
      const tag = existingList.tagName.toLowerCase();
      const sameType = (type === 'ul' && tag === 'ul') || (type === 'ol' && tag === 'ol');
      if (sameType) {
        // Toggle off - unwrap only this list's items back to plain divs
        const liItems = Array.from(existingList.querySelectorAll(':scope > li'));
        const fragment = document.createDocumentFragment();
        liItems.forEach(li => {
          const div = document.createElement('div');
          div.innerHTML = li.innerHTML || '<br>';
          fragment.appendChild(div);
        });
        existingList.replaceWith(fragment);
      } else {
        // Switch type (ul ↔ ol) - change only THIS list element, not others
        const newList = document.createElement(type);
        // Copy attributes (e.g. style) and inner HTML
        Array.from(existingList.attributes).forEach(attr => newList.setAttribute(attr.name, attr.value));
        newList.innerHTML = existingList.innerHTML;
        existingList.replaceWith(newList);
        // Restore cursor inside new list
        const firstLi = newList.querySelector('li');
        if (firstLi) {
          const newRange = document.createRange();
          newRange.selectNodeContents(firstLi);
          newRange.collapse(false);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      }
      handleInput();
      return;
    }

    // No existing list - insert one. Try execCommand first; fall back to insertHTML.
    const cmd = type === 'ul' ? 'insertUnorderedList' : 'insertOrderedList';
    const before = el.innerHTML;
    document.execCommand(cmd, false, null);
    if (el.innerHTML === before) {
      // execCommand was a no-op: fall back to insertHTML
      const text = sel.isCollapsed ? 'List item' : sel.toString();
      if (!sel.isCollapsed) range.deleteContents();
      document.execCommand('insertHTML', false,
        `<${type}><li>${text}</li></${type}><div><br></div>`);
    }
    handleInput();
  }, [edit, handleInput]);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  }, []);
  const restoreSelection = useCallback(() => {
    const range = savedRangeRef.current;
    if (!range) return;
    const sel = window.getSelection();
    if (sel) { sel.removeAllRanges(); sel.addRange(range); }
  }, []);

  // ── Image resize overlay ──────────────────────────────────────────────────
  // Key insight: use offsetTop/offsetLeft walking up to wrapRef, NOT getBoundingClientRect.
  // This gives coords that are always relative to the wrapper, regardless of page scroll
  // or editor scroll position.

  const getImageBoxRelativeToWrap = useCallback((img) => {
    const wrap = wrapRef.current;
    if (!img || !wrap) return null;

    // getBoundingClientRect gives viewport-relative coords for both elements.
    // Subtracting gives coords relative to the wrapper - correct regardless of
    // page scroll, editor scroll, or contenteditable offsetParent quirks.
    const wrapRect = wrap.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    return {
      top: imgRect.top - wrapRect.top,
      left: imgRect.left - wrapRect.left,
      width: imgRect.width,
      height: imgRect.height,
    };
  }, []);

  // Recompute box on editor scroll so handle stays on the image
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const onScroll = () => {
      if (selectedImage) setImageBox(getImageBoxRelativeToWrap(selectedImage));
    };
    editor.addEventListener('scroll', onScroll);
    return () => editor.removeEventListener('scroll', onScroll);
  }, [selectedImage, getImageBoxRelativeToWrap]);

  // Click inside editor - select image or deselect
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleClick = (e) => {
      editor.querySelectorAll('img').forEach((img) => {
        img.style.outline = '';
      });

      if (e.target.tagName === 'IMG') {
        e.target.style.outline = '2px solid #1677ff';
        setSelectedImage(e.target);
        setImageBox(getImageBoxRelativeToWrap(e.target));
      } else {
        setSelectedImage(null);
        setImageBox(null);
      }
    };

    editor.addEventListener('click', handleClick);
    return () => editor.removeEventListener('click', handleClick);
  }, [getImageBoxRelativeToWrap]);

  // Global mousemove / mouseup for dragging the resize handle
  useEffect(() => {
    const onMove = (e) => {
      if (!resizeStateRef.current) return;
      const { startX, startWidth, image } = resizeStateRef.current;
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      image.style.width = `${newWidth}px`;
      image.style.height = 'auto';
      // Update overlay live
      setImageBox(getImageBoxRelativeToWrap(image));
    };
    const onUp = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      handleInput();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [getImageBoxRelativeToWrap, handleInput]);

  // Delete selected image with Backspace / Delete
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedImage) {
        e.preventDefault();
        selectedImage.remove();
        setSelectedImage(null);
        setImageBox(null);
        handleInput();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedImage, handleInput]);

  // ── Text / block formatting ───────────────────────────────────────────────

  const setBlockType = useCallback((tag) => { exec('formatBlock', tag); }, [exec]);
  const setFontFamily = useCallback((family) => { exec('fontName', family); }, [exec]);
  const setFontSize = useCallback((px) => {
    if (!edit) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    // const sel = window.getSelection();

    // Map px value to one of the 7 legacy font sizes (1-7) so execCommand
    // registers the change, then immediately override with the exact px value.
    // This approach works for both increasing AND decreasing, and handles
    // collapsed cursors (no selection) correctly.
    const pxNum = parseFloat(px);
    let legacySize = 3; // default ~12px
    if (pxNum <= 10) legacySize = 1;
    else if (pxNum <= 13) legacySize = 2;
    else if (pxNum <= 16) legacySize = 3;
    else if (pxNum <= 18) legacySize = 4;
    else if (pxNum <= 24) legacySize = 5;
    else if (pxNum <= 32) legacySize = 6;
    else legacySize = 7;

    document.execCommand('fontSize', false, String(legacySize));

    // execCommand wraps selection (or caret) in <font size="N"> - replace with
    // a <span style="font-size:Npx"> so the exact size is applied.
    el.querySelectorAll('font[size]').forEach((font) => {
      const span = document.createElement('span');
      span.style.fontSize = px;
      // Move children into the span
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);
    });

    handleInput();
  }, [edit, handleInput]);

  const applyTextColor = useCallback((color) => { restoreSelection(); exec('foreColor', color); }, [exec, restoreSelection]);
  const applyHighlight = useCallback((color) => { restoreSelection(); exec('hiliteColor', color); }, [exec, restoreSelection]);

  const insertHtml = useCallback((html) => {
    if (!edit) return;
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, html);
    handleInput();
  }, [edit, handleInput]);

  const insertHr = useCallback(() => {
    insertHtml('<hr style="border:none;border-top:1px solid #e0e0e0;margin:16px 0;"><div><br></div>');
  }, [insertHtml]);

  // ── Link modal ────────────────────────────────────────────────────────────

  const openLinkModal = useCallback(() => {
    saveSelection();
    const sel = window.getSelection();
    let anchor = null;
    if (sel && sel.rangeCount) {
      let node = sel.getRangeAt(0).commonAncestorContainer;
      if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
      anchor = node.closest?.('a') || null;
    }
    const selText = sel ? sel.toString() : '';
    setLinkInitial({ url: anchor ? anchor.getAttribute('href') || '' : '', text: anchor ? anchor.textContent || '' : selText, title: anchor ? anchor.getAttribute('title') || '' : '', isEdit: !!anchor, anchor });
    openModal('link');
  }, [saveSelection]);

  const handleLinkInsert = useCallback(({ url, text, title }) => {
    restoreSelection();
    editorRef.current?.focus();
    const titleAttr = title ? ` title="${title}"` : '';
    const initial = linkInitial;
    if (initial?.anchor) {
      const a = initial.anchor;
      a.setAttribute('href', url); a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer');
      if (title) a.setAttribute('title', title); else a.removeAttribute('title');
      if (text) a.textContent = text;
    } else {
      insertHtml(`<a href="${url}"${titleAttr} target="_blank" rel="noopener noreferrer">${text || url}</a>`);
    }
    handleInput();
  }, [restoreSelection, linkInitial, insertHtml, handleInput]);

  const triggerImagePicker = useCallback(() => { fileInputRef.current?.click(); }, []);
  const handleImageFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!validateImageFile(file)) return;
    try {
      const { url, name } = await uploadImage(file);
      insertImageIntoEditor(editorRef.current, url, name);
      handleInput();
    } catch (err) {
      message.error(err?.response?.data?.message?.message || 'Image upload failed');
    }
  }, [uploadImage, insertImageIntoEditor, handleInput]);

  const handleImageUrlInsert = useCallback(({ url, alt, width }) => {
    restoreSelection();
    const widthStyle = width ? `width:${width};` : '';
    insertHtml(`<img src="${url}" alt="${alt || ''}" style="max-width:100%;${widthStyle}height:auto;display:inline-block;" />`);
  }, [insertHtml, restoreSelection]);

  const triggerAttachmentPicker = useCallback(() => { attachInputRef.current?.click(); }, []);
  const handleAttachmentChange = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const validFiles = files.filter(validateAttachmentFile);
    if (!validFiles.length) return;
    try {
      const buffered = await Promise.all(validFiles.map(async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        return { filename: file.name, contentType: file.type, size: file.size, contentDisposition: 'attachment', content: { type: 'Buffer', data: Array.from(new Uint8Array(arrayBuffer)) } };
      }));
      setFiles(prev => [...prev, ...buffered]);
      message.success(`${validFiles.length} file${validFiles.length > 1 ? 's' : ''} attached`);
    } catch (_) { message.error('Failed to read attachment'); }
  }, [setFiles]);

  const handleTableInsert = useCallback((opts) => { restoreSelection(); insertHtml(buildTableHtml(opts)); }, [restoreSelection, insertHtml]);
  const handleEmojiSelect = useCallback((emoji) => { restoreSelection(); exec('insertText', emoji); }, [restoreSelection, exec]);

  // Paste handler
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const onPaste = async (e) => {
      if (!edit) { e.preventDefault(); return; }

      // ── Check for any pasted file first (image or non-image)
      const fileItem = Array.from(e.clipboardData.items || []).find(
        item => item.kind === 'file'
      );
      if (fileItem) {
        e.preventDefault();
        const file = fileItem.getAsFile();
        if (!file || !validateImageFile(file)) return; // shows error toast if not png/jpg/jpeg
        const tempId = `img-paste-${Date.now()}`;
        document.execCommand('insertHTML', false,
          `<img id="${tempId}" src="" data-uploading="true" style="max-width:100%;height:auto;opacity:0.4;outline:2px dashed #1677ff;" />`
        );
        const imgEl = el.querySelector(`#${tempId}`);
        if (imgEl) {
          try {
            const { url } = await uploadImage(file);
            imgEl.src = url;
            imgEl.removeAttribute('data-uploading');
            imgEl.style.opacity = '';
            imgEl.style.outline = '';
            imgEl.removeAttribute('id');
          } catch (err) {
            message.error(err?.response?.data?.message?.message || 'Image upload failed');
            imgEl.remove();
          }
          handleInput();
        }
        return;
      }

      // ── Case 2: HTML paste (from browser, Word, etc.) - may contain blob: img srcs
      const html = e.clipboardData.getData('text/html');
      if (html) {
        e.preventDefault();
        const clean = html
          .replace(/<o:p>[\s\S]*?<\/o:p>/gi, '')
          .replace(/class="[^"]*mso[^"]*"/gi, '')
          .replace(/style="[^"]*mso[^"]*"/gi, '')
          .replace(/<span[^>]*>\s*<\/span>/gi, '');
        document.execCommand('insertHTML', false, clean);

        // Upload any blob: images that came in via HTML - use Promise.all, not forEach
        setTimeout(async () => {
          const imgs = Array.from(el.querySelectorAll('img[src^="blob:"]'));
          await Promise.all(imgs.map(async (img) => {
            if (img.dataset.uploading) return;
            img.dataset.uploading = 'true';
            try {
              const blob = await fetch(img.src).then(r => r.blob());
              const ext = blob.type.split('/')[1] || 'png';
              const file = new File([blob], `pasted-${Date.now()}.${ext}`, { type: blob.type });
              if (!validateImageFile(file)) { img.remove(); return; }
              await replacePlaceholder(file, img);
            } catch (_) { img.remove(); }
          }));
          handleInput();
        }, 80);
        // return;
      }

      // ── Case 3: plain text - let browser handle normally
    };
    el.addEventListener('paste', onPaste);
    return () => el.removeEventListener('paste', onPaste);
  }, [edit, handleInput, replacePlaceholder, uploadImage]);

  // Drop handler
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const onDrop = async (e) => {
      if (!edit) return;
      const allFiles = Array.from(e.dataTransfer?.files || []);
      if (!allFiles.length) return;
      const files = allFiles.filter(f => validateImageFile(f));
      if (!files.length) return;
      e.preventDefault();
      files.forEach((file) => {
        const tempId = `img-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        document.execCommand(
          'insertHTML',
          false,
          `<img id="${tempId}" src="" data-uploading="true" style="max-width:100%;height:auto;opacity:0.4;outline:2px dashed #1677ff;" />`
        );
        const imgEl = el.querySelector(`#${tempId}`);
        if (imgEl) {
          replacePlaceholder(file, imgEl)
            .then(() => {
              imgEl.style.opacity = '';
              imgEl.style.outline = '';
              imgEl.removeAttribute('id');
            })
            .catch((err) => {
              message.error(err?.response?.data?.message?.message || 'Image upload failed');
              imgEl.remove();
            })
            .finally(() => {
              handleInput();
            });
        }
      });
    };
    const onDragOver = (e) => { if (edit) e.preventDefault(); };
    el.addEventListener('drop', onDrop);
    el.addEventListener('dragover', onDragOver);
    return () => { el.removeEventListener('drop', onDrop); el.removeEventListener('dragover', onDragOver); };
  }, [edit, handleInput, replacePlaceholder]);

  // ── Exit list on Enter at empty list item ─────────────────────────────────
  // Pressing Enter on a blank <li> should break out of the list and insert a
  // normal paragraph, instead of adding another empty bullet/number.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return;
      let node = range.startContainer;
      if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
      const li = node.closest?.('li');
      if (!li) return;
      // Check if the <li> is effectively empty (only whitespace / <br>)
      const liText = li.innerText?.trim();
      if (liText !== '') return;
      // Empty <li> - exit the list
      e.preventDefault();
      const list = li.closest('ul,ol');
      if (!list) return;
      li.remove();
      // Insert a new empty div after the list
      const div = document.createElement('div');
      div.innerHTML = '<br>';
      list.insertAdjacentElement('afterend', div);
      // Move cursor into the new div
      const newRange = document.createRange();
      newRange.setStart(div, 0);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
      handleInput();
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [handleInput]);

  // Protect reply/forward blocks from accidental deletion
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      const node = sel.anchorNode;
      [el.querySelector('#ReplyContent'), el.querySelector('#ForwardContent')].filter(Boolean).some(block => {
        const atStart = range.startOffset === 0;
        const atEnd = range.startOffset === (node?.textContent?.length ?? 0);
        if ((e.key === 'Backspace' && atStart && node?.nextSibling === block) || (e.key === 'Delete' && atEnd && node?.previousSibling === block)) { e.preventDefault(); return true; }
        return false;
      });
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, []);

  // ── Toolbar config ────────────────────────────────────────────────────────

  const toolbarConfig = useMemo(() => ({
    showAttachment: page === 'Email',
    // showSignature: page === 'Email',
    // showUnsubscribe: page === 'Email' || page === 'Template',
    // showVariables: page === 'Template',
    // showTable: ['Signature', 'TextBox'].includes(page),
    isMinimal: page === 'TextBoxs',
    toolbarPosition: page === 'Template' ? 'top' : 'bottom',
  }), [page]);

  const isDisabled = !edit || (page !== 'TextBoxs' && !selectedEntity);

  // ── currentFont state tracks the editor base font for FontDropdown ────────
  const [currentFont, setCurrentFont] = useState('inherit');

  const applyFontFamily = useCallback((family) => {
    setCurrentFont(family);
    restoreSelection();
    setFontFamily(family);
  }, [setFontFamily, restoreSelection]);

  const toolbar = (
    <div style={S.toolbar}>
      {/* History */}
      <TbBtn title="Undo" icon={<FeatherIcon icon="rotate-ccw" size={14} />} onClick={() => exec('undo')} disabled={isDisabled} />
      <TbBtn title="Redo" icon={<FeatherIcon icon="rotate-cw" size={14} />} onClick={() => exec('redo')} disabled={isDisabled} />
      <Sep />

      {/* Attachment (Email only) */}
      {toolbarConfig.showAttachment && (
        <>
          <TbBtn
            title="Attach file"
            icon={<FeatherIcon icon="paperclip" size={14} />}
            onClick={triggerAttachmentPicker}
            disabled={isDisabled}
          />
        </>
      )}

      {/* Image */}
      <TbBtn
        title="Insert image (upload)"
        icon={<FeatherIcon icon="image" size={14} />}
        onClick={triggerImagePicker}
        disabled={isDisabled}
      />

      {/* Link */}
      <TbBtn
        title="Insert / edit link"
        icon={<FeatherIcon icon="link" size={14} />}
        onClick={openLinkModal}
        disabled={isDisabled}
      />
      {/* <TbBtn title="Remove link" icon={<FeatherIcon icon="link-2-off" size={14} />} onClick={() => exec('unlink')} disabled={isDisabled} /> */}
      <Sep />

      {/* Block type */}
      <select
        style={{ ...S.tbSelect, width: 110 }}
        disabled={isDisabled}
        onMouseDown={saveSelection}
        onChange={e => { setBlockType(e.target.value); }}
        defaultValue="div"
      >
        {BLOCK_TYPES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
      </select>

      {/* ── Font family - scrollable grouped dropdown ── */}
      <FontDropdown
        currentFont={currentFont}
        onOpen={saveSelection}
        onSelect={(family) => { applyFontFamily(family); }}
        disabled={isDisabled}
      />

      {/* Font size */}
      <select
        style={{ ...S.tbSelect, width: 56 }}
        disabled={isDisabled}
        onMouseDown={saveSelection}
        onChange={e => { setFontSize(`${e.target.value}px`) }}
        defaultValue="14"
      >
        {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <Sep />

      {/* Text formatting */}
      <TbBtn title="Bold" icon={<FeatherIcon icon="bold" size={14} />} active={activeFormats.bold} onClick={() => exec('bold')} disabled={isDisabled} />
      <TbBtn title="Italic" icon={<FeatherIcon icon="italic" size={14} />} active={activeFormats.italic} onClick={() => exec('italic')} disabled={isDisabled} />
      <TbBtn title="Underline" icon={<FeatherIcon icon="underline" size={14} />} active={activeFormats.underline} onClick={() => exec('underline')} disabled={isDisabled} />
      <TbBtn title="Strikethrough" icon={<StrikeThrougIcon width='14' height='14' strokeWidth='1' />} active={activeFormats.strikethrough} onClick={() => exec('strikethrough')} disabled={isDisabled} />
      <Sep />

      {/* Colour */}
      <TbBtn title="Text colour" icon={<span style={{ borderBottom: '3px solid #e53e3e', lineHeight: 1 }}>A</span>} onClick={() => { saveSelection(); openModal('textColor'); }} disabled={isDisabled} />
      <TbBtn title="Highlight colour" icon={<span style={{ background: '#fef08a', padding: '0 2px', lineHeight: 1 }}>H</span>} onClick={() => { saveSelection(); openModal('highlightColor'); }} disabled={isDisabled} />
      <Sep />

      {/* ── Alignment group dropdown ── */}
      <ToolbarDropdown
        label="Align"
        icon={<FeatherIcon icon="align-left" size={13} />}
        disabled={isDisabled}
        items={[
          { label: 'Align left', icon: <FeatherIcon icon="align-left" size={13} />, active: activeFormats.justifyLeft, onClick: () => applyAlign('left') },
          { label: 'Align center', icon: <FeatherIcon icon="align-center" size={13} />, active: activeFormats.justifyCenter, onClick: () => applyAlign('center') },
          { label: 'Align right', icon: <FeatherIcon icon="align-right" size={13} />, active: activeFormats.justifyRight, onClick: () => applyAlign('right') },
          { label: 'Justify', icon: <FeatherIcon icon="align-justify" size={13} />, active: activeFormats.justifyFull, onClick: () => applyAlign('justify') },
        ]}
      />

      {/* ── Lists & indent group dropdown ── */}
      <ToolbarDropdown
        label="List"
        icon={<FeatherIcon icon="list" size={13} />}
        disabled={isDisabled}
        items={[
          { label: 'Bullet list', icon: <FeatherIcon icon="list" size={13} />, active: activeFormats.insertUnorderedList, onClick: () => toggleList('ul') },
          { label: 'Numbered list', icon: <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.5px' }}>1≡</span>, active: activeFormats.insertOrderedList, onClick: () => toggleList('ol') },
          { label: 'Indent', icon: <FeatherIcon icon="maximize-2" size={13} />, active: false, onClick: () => exec('indent') },
          { label: 'Outdent', icon: <FeatherIcon icon="minimize-2" size={13} />, active: false, onClick: () => exec('outdent') },
        ]}
      />
      <Sep />

      {/* ── Insert group dropdown ── */}
      <ToolbarDropdown
        label="Insert"
        icon={<FeatherIcon icon="plus" size={13} />}
        disabled={isDisabled}
        items={[
          { label: 'Table', icon: <FeatherIcon icon="grid" size={13} />, active: false, onClick: () => { saveSelection(); openModal('table'); } },
          { label: 'Horizontal rule', icon: <FeatherIcon icon="minus" size={13} />, active: false, onClick: insertHr },
          { label: 'Emoji', icon: <FeatherIcon icon="smile" size={13} />, active: false, onClick: () => { saveSelection(); openModal('emoji'); } },
        ]}
      />
      <Sep />

      {/* Clear formatting */}
      <TbBtn title="Clear formatting" icon={<span style={{ fontSize: 12, fontWeight: 600 }}>Tx</span>} onClick={() => exec('removeFormat')} disabled={isDisabled} />
    </div>
  );

  // ── Minimal toolbar (TextBoxs page) ───────────────────────────────────────
  const minimalToolbar = (
    <div style={S.toolbar}>
      {/* Font size */}
      <select
        style={{ ...S.tbSelect, width: 56 }}
        disabled={isDisabled}
        onMouseDown={saveSelection}
        onChange={e => { setFontSize(`${e.target.value}px`); }}
        defaultValue="14"
      >
        {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <Sep />

      {/* Text formatting */}
      <TbBtn title="Bold" icon={<FeatherIcon icon="bold" size={14} />} active={activeFormats.bold} onClick={() => exec('bold')} disabled={isDisabled} />
      <TbBtn title="Italic" icon={<FeatherIcon icon="italic" size={14} />} active={activeFormats.italic} onClick={() => exec('italic')} disabled={isDisabled} />
      <TbBtn title="Underline" icon={<FeatherIcon icon="underline" size={14} />} active={activeFormats.underline} onClick={() => exec('underline')} disabled={isDisabled} />
      <TbBtn title="Strikethrough" icon={<StrikeThrougIcon width='14' height='14' strokeWidth='1' />} active={activeFormats.strikethrough} onClick={() => exec('strikethrough')} disabled={isDisabled} />
      <Sep />

      {/* Text colour + background colour */}
      <TbBtn title="Text colour" icon={<span style={{ borderBottom: '3px solid #e53e3e', lineHeight: 1 }}>A</span>} onClick={() => { saveSelection(); openModal('textColor'); }} disabled={isDisabled} />
      <TbBtn title="Text background colour" icon={<span style={{ background: '#fef08a', padding: '0 2px', lineHeight: 1 }}>H</span>} onClick={() => { saveSelection(); openModal('highlightColor'); }} disabled={isDisabled} />
      <Sep />

      {/* Link */}
      <TbBtn title="Insert / edit link" icon={<FeatherIcon icon="link" size={14} />} onClick={openLinkModal} disabled={isDisabled} />
      <TbBtn
        title="Remove link"
        icon={
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <FeatherIcon icon="link" size={14} />
            <span style={{ position: 'absolute', left: -1, right: -1, top: '50%', borderTop: '1.5px solid currentColor', transform: 'rotate(-45deg)' }} />
          </span>
        }
        onClick={() => exec('unlink')}
        disabled={isDisabled}
      />
      <Sep />

      {/* Lists & indent */}
      <TbBtn title="Bullet list" icon={<FeatherIcon icon="list" size={14} />} active={activeFormats.insertUnorderedList} onClick={() => toggleList('ul')} disabled={isDisabled} />
      <TbBtn title="Numbered list" icon={<span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.5px' }}>1≡</span>} active={activeFormats.insertOrderedList} onClick={() => toggleList('ol')} disabled={isDisabled} />
      <TbBtn title="Indent" icon={<FeatherIcon icon="chevrons-right" size={14} />} onClick={() => exec('indent')} disabled={isDisabled} />
      <TbBtn title="Outdent" icon={<FeatherIcon icon="chevrons-left" size={14} />} onClick={() => exec('outdent')} disabled={isDisabled} />
      <Sep />

      {/* Clear formatting */}
      <TbBtn title="Clear formatting" icon={<span style={{ fontSize: 12, fontWeight: 600 }}>Tx</span>} onClick={() => exec('removeFormat')} disabled={isDisabled} />
    </div>
  );

  const activeToolbar = toolbarConfig.isMinimal ? minimalToolbar : toolbar;

  return (
    <div ref={shellRef} style={S.shell(customHeight || height)}>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageFileChange} />
      <input ref={attachInputRef} type="file" accept="*" style={{ display: 'none' }} multiple onChange={handleAttachmentChange} />

      {toolbarConfig.toolbarPosition === 'top' && activeToolbar}

      <style>{`
        [contenteditable]:empty:before { content: attr(data-placeholder); color: #bfbfbf; pointer-events: none; }
        [contenteditable] a { color: #1677ff; }
        [contenteditable] p, [contenteditable] div { margin: 0; padding: 0; }
        [contenteditable] blockquote { border-left: 3px solid #1677ff !important; margin: 12px 0; padding: 8px 14px; background: #f0f7ff; color: #595959; font-style: italic; }
        [contenteditable] pre { background: #1e1e2e; color: #cdd6f4; padding: 12px 16px; border-radius: 5px; font-family: 'Courier New', monospace; font-size: 13px; overflow-x: auto; }
        [contenteditable] table { border-collapse: collapse; width: 100%; }
        [contenteditable] th, [contenteditable] td { border: 1px solid #d9d9d9; padding: 8px 12px; }
        [contenteditable] th { background: #fafafa; font-weight: 600; }
        [contenteditable] img { max-width: 100%; height: auto; }
        [contenteditable] hr { border: none; border-top: 1px solid #e0e0e0; margin: 16px 0; }
        [contenteditable] [data-signature="true"] { margin-top: 20px; padding-top: 14px; border-top: 1px solid #e0e0e0; }
        [contenteditable] ul { list-style-type: disc; padding-left: 24px; margin: 6px 0; }
        [contenteditable] ol { list-style-type: decimal; padding-left: 24px; margin: 6px 0; }
        [contenteditable] li { margin: 2px 0; }
      `}</style>

      {/* ── Editor wrap: position:relative so overlay coords are relative to it ── */}
      <div ref={wrapRef} style={S.editorWrap}>
        <div role="button" aria-label="editor"
          tabIndex={0}
          ref={editorRef}
          contentEditable={!isDisabled}
          suppressContentEditableWarning
          data-placeholder={placeholder || 'Start writing…'}
          style={S.editor(isDisabled)}
          onInput={handleInput}
          onKeyUp={syncFormats}
          onMouseUp={syncFormats}
          onFocus={syncFormats}
        />

        {/* ── Resize overlay - absolutely positioned inside wrapRef ── */}
        {selectedImage && imageBox && (
          <div
            style={{
              position: 'absolute',
              left: imageBox.left,
              top: imageBox.top,
              width: imageBox.width,
              height: imageBox.height,
              border: '2px solid #1677ff',
              pointerEvents: 'none',
              zIndex: 10,
              boxSizing: 'border-box',
            }}
          >
            {/* SE resize handle */}
            <div
              style={{
                position: 'absolute',
                width: 10,
                height: 10,
                right: -5,
                bottom: -5,
                background: '#1677ff',
                borderRadius: '50%',
                cursor: 'nwse-resize',
                pointerEvents: 'auto',
              }}
              role="button"
              tabIndex={0} aria-label="resize"
              onMouseDown={(e) => {
                e.preventDefault();
                resizeStateRef.current = {
                  startX: e.clientX,
                  startWidth: selectedImage.offsetWidth,
                  image: selectedImage,
                };
              }}
            />
          </div>
        )}
      </div>

      {toolbarConfig.toolbarPosition === 'bottom' && activeToolbar}

      {/* <div style={S.statusbar}>
        <span>{wordCount} word{wordCount !== 1 ? 's' : ''}</span>
      </div> */}

      {/* ── Drag handle to resize the editor box ── */}
      {page === 'TextBoxs' && (<div
        style={S.resizeHandle}
        title="Drag to resize"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize editor"
        onPointerDown={startBoxResize}
      >
        <div style={S.resizeGrip} />
      </div>
      )}

      {page === 'Template' && (
        <VariableAutocomplete editorRef={editorRef} attributesRef={attributesRef} loadingAttributes={loadingAttributes} enabled={edit && !!selectedEntity} />
      )}

      <LinkModal IsTrackUrl={toolbarConfig.isMinimal ? 'No' : IsTrackUrl} open={modals.link} onClose={() => closeModal('link')} onInsert={handleLinkInsert} clickTrackUrl={toolbarConfig.isMinimal ? '' : clickTrackUrl} initialData={linkInitial} />
      <ImageUrlModal open={modals.imageUrl} onClose={() => closeModal('imageUrl')} onInsert={handleImageUrlInsert} />
      <TableModal open={modals.table} onClose={() => closeModal('table')} onInsert={handleTableInsert} />
      <ColorPickerModal open={modals.textColor} onClose={() => closeModal('textColor')} onSelect={applyTextColor} title="Text colour" />
      <ColorPickerModal open={modals.highlightColor} onClose={() => closeModal('highlightColor')} onSelect={applyHighlight} title="Highlight colour" />
      <EmojiModal open={modals.emoji} onClose={() => closeModal('emoji')} onSelect={handleEmojiSelect} />
    </div>
  );
}

RichEmailEditor.propTypes = {
  setContent: PropTypes.func.isRequired,
  setFiles: PropTypes.func.isRequired,
  content: PropTypes.string.isRequired,
  edit: PropTypes.bool.isRequired,
  page: PropTypes.string.isRequired,
  selectedEntity: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  placeholder: PropTypes.string,
  IsTrackUrl: PropTypes.string,
};

RichEmailEditor.defaultProps = {
  placeholder: '',
};

export default RichEmailEditor;